"use client";

import { useRef, type CSSProperties } from "react";
import {
    resizeBox,
    rotateBox,
    type ResizeHandle,
} from "@/features/lomdaEditorV2/lib/elements";
import {
    useInteractDraggable,
    type GestureEvent,
    type GestureWork,
    type SnapSpec,
} from "./useCanvasGesture";

interface Controller {
    start: () => void;
    update: (
        mutate: (w: GestureWork) => ReturnType<typeof resizeBox>,
        snap?: SnapSpec
    ) => void;
    end: () => void;
}

const HANDLES: { id: ResizeHandle; style: CSSProperties; cursor: string }[] = [
    { id: "nw", style: { left: 0, top: 0 }, cursor: "nwse-resize" },
    { id: "n", style: { left: "50%", top: 0 }, cursor: "ns-resize" },
    { id: "ne", style: { left: "100%", top: 0 }, cursor: "nesw-resize" },
    { id: "e", style: { left: "100%", top: "50%" }, cursor: "ew-resize" },
    { id: "se", style: { left: "100%", top: "100%" }, cursor: "nwse-resize" },
    { id: "s", style: { left: "50%", top: "100%" }, cursor: "ns-resize" },
    { id: "sw", style: { left: 0, top: "100%" }, cursor: "nesw-resize" },
    { id: "w", style: { left: 0, top: "50%" }, cursor: "ew-resize" },
];

const isCorner = (h: ResizeHandle) => h.length === 2;

// No native "rotate" cursor exists — use a circular-arrow icon (white halo so
// it reads on any background), hotspot at its center, with `grab` as fallback.
const ROTATE_SVG =
    "<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='none' stroke-linecap='round' stroke-linejoin='round'>" +
    "<g stroke='white' stroke-width='4'><path d='M21 12a9 9 0 1 1-3-6.7'/><path d='M21 3v6h-6'/></g>" +
    "<g stroke='black' stroke-width='2'><path d='M21 12a9 9 0 1 1-3-6.7'/><path d='M21 3v6h-6'/></g></svg>";
const ROTATE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(ROTATE_SVG)}") 14 14, grab`;

function Handle({
    style,
    cursor,
    onStart,
    onMove,
    onEnd,
}: {
    style: CSSProperties;
    cursor: string;
    onStart: () => void;
    onMove: (e: GestureEvent) => void;
    onEnd: () => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    useInteractDraggable(ref, true, onStart, onMove, onEnd, undefined, cursor);
    return (
        <div
            ref={ref}
            data-canvas-handle
            onPointerDown={(e) => e.stopPropagation()}
            className="pointer-events-auto absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-[2px] border border-primary bg-background shadow-sm touch-none"
            style={{ ...style, cursor }}
        />
    );
}

export function SelectionFrame({
    controller,
    rotation,
    showAngle,
}: {
    controller: Controller;
    rotation: number;
    showAngle: boolean;
}) {
    return (
        <div className="pointer-events-none absolute inset-0 z-[5]">
            <div className="absolute inset-0 outline outline-2 outline-primary" />
            <div
                className="absolute left-1/2 top-0 h-7 w-px -translate-x-1/2 -translate-y-full bg-primary"
                aria-hidden
            />
            <RotateHandle controller={controller} />
            {showAngle && <AngleBadge rotation={rotation} />}
            {HANDLES.map((h) => (
                <Handle
                    key={h.id}
                    style={h.style}
                    cursor={h.cursor}
                    onStart={controller.start}
                    onMove={(e) =>
                        controller.update(
                            (w) =>
                                resizeBox(
                                    w.box,
                                    h.id,
                                    e.dx * w.scale,
                                    e.dy * w.scale,
                                    isCorner(h.id) && e.shiftKey
                                ),
                            {
                                kind: "resize",
                                handle: h.id,
                                disable: e.altKey,
                                aspect: isCorner(h.id) && e.shiftKey,
                            }
                        )
                    }
                    onEnd={controller.end}
                />
            ))}
        </div>
    );
}

// Live rotation readout, counter-rotated so it stays upright while the box (and
// this frame) spin underneath it.
function AngleBadge({ rotation }: { rotation: number }) {
    return (
        <div
            className="absolute left-1/2 top-0"
            style={{
                transform: `translate(-50%, -56px) rotate(${-rotation}deg)`,
            }}
            aria-hidden
        >
            <div className="rounded bg-primary px-1.5 py-0.5 text-[11px] font-medium leading-none text-primary-foreground shadow-sm">
                {Math.round(rotation)}°
            </div>
        </div>
    );
}

function RotateHandle({ controller }: { controller: Controller }) {
    const ref = useRef<HTMLDivElement>(null);
    useInteractDraggable(
        ref,
        true,
        controller.start,
        (e) =>
            controller.update(
                (w) => ({
                    ...w.box,
                    rotation: rotateBox(
                        w.box,
                        (e.clientX - w.rect.left) * w.scale,
                        (e.clientY - w.rect.top) * w.scale,
                        e.altKey
                    ),
                }),
                { kind: "rotate", disable: e.altKey }
            ),
        controller.end,
        undefined,
        ROTATE_CURSOR
    );
    return (
        <div
            ref={ref}
            data-canvas-handle
            onPointerDown={(e) => e.stopPropagation()}
            style={{ cursor: ROTATE_CURSOR }}
            className="pointer-events-auto absolute left-1/2 top-0 size-3.5 -translate-x-1/2 -translate-y-[34px] rounded-full border border-primary bg-background shadow-sm touch-none"
        />
    );
}
